import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Plus, Trash2 } from "lucide-react"
import { useState } from "react"

import { Heading } from "@/components/heading"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"
import { formatFullGold, formatGold } from "@/lib/format"
import { cn } from "@/lib/utils"
import {
  leaguePath,
  leagueTeamPath,
  leagueTeamPlayerPath,
  leagueTeamPlayersPath,
  purchaseRerollLeagueTeamPath,
} from "@/routes"
import type { BreadcrumbItem, PositionDef, TeamManageProps } from "@/types"

import { FormErrorSummary } from "./form-error-summary"
import { PositionalShop } from "./positional-shop"
import { StaffExtrasCard } from "./staff-extras-card"

export default function TeamManage({
  league,
  team,
  positions,
  roster_counts,
  errors,
}: TeamManageProps) {
  const [hiringPosition, setHiringPosition] = useState<string | null>(null)
  const [playerName, setPlayerName] = useState("")
  const [firePlayerId, setFirePlayerId] = useState<string | null>(null)

  const alivePlayerCount = team.players.filter(
    (p) => p.status !== "dead",
  ).length
  const rosterFull = alivePlayerCount >= 16
  const canFire = alivePlayerCount > 11

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: league.name,
      href: leaguePath(league.id),
      leagueSwitcher: true,
    },
    { title: team.name, href: leagueTeamPath(league.id, team.id) },
    { title: "Manage" },
  ]

  const rerollPurchaseCost = team.reroll_cost * 2

  function handleStartHire(position: PositionDef) {
    const nextNumber = (roster_counts[position.name] ?? 0) + 1
    setPlayerName(`${position.name} ${nextNumber}`)
    setHiringPosition(position.name)
  }

  function handleConfirmHire() {
    if (!hiringPosition || !playerName.trim()) return
    router.post(leagueTeamPlayersPath(league.id, team.id), {
      position: hiringPosition,
      name: playerName.trim(),
    })
    setHiringPosition(null)
    setPlayerName("")
  }

  function handleCancelHire() {
    setHiringPosition(null)
    setPlayerName("")
  }

  function handleFire(playerId: string) {
    router.delete(leagueTeamPlayerPath(league.id, team.id, playerId))
    setFirePlayerId(null)
  }

  function handlePurchaseReroll() {
    router.post(purchaseRerollLeagueTeamPath(league.id, team.id))
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Manage ${team.name}`} />

      <div className="mx-auto w-full max-w-7xl px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <Heading level={1} wrapperClassName="mb-0">
            Manage Team
          </Heading>
          <Button asChild variant="outline" size="sm">
            <Link href={leagueTeamPath(league.id, team.id)}>
              <ArrowLeft className="mr-1.5 size-3.5" />
              Back to Team
            </Link>
          </Button>
        </div>

        <FormErrorSummary errors={errors} />

        <div className="grid gap-10 lg:grid-cols-5">
          {/* Left: Positional Shop */}
          <div className="lg:col-span-3">
            <PositionalShop
              positions={positions}
              count={(position) => roster_counts[position.name] ?? 0}
              hireDisabled={(position) =>
                (roster_counts[position.name] ?? 0) >= position.quantity ||
                rosterFull ||
                team.treasury_balance < position.cost
              }
              onHire={handleStartHire}
              renderExpanded={(position, isLast) =>
                hiringPosition === position.name ? (
                  <div
                    className={cn(
                      "flex items-center gap-2 bg-[oklch(0.12_0.005_230)] px-3 py-2.5",
                      !isLast && "border-b",
                    )}
                    style={{ borderColor: "var(--border)" }}
                  >
                    <Input
                      value={playerName}
                      onChange={(e) => setPlayerName(e.target.value)}
                      placeholder="Player name"
                      className="h-8 flex-1 text-sm"
                      aria-label="Player name"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleConfirmHire()
                        if (e.key === "Escape") handleCancelHire()
                      }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleConfirmHire}
                      disabled={!playerName.trim()}
                      className="text-xs"
                    >
                      Confirm
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleCancelHire}
                      className="text-xs"
                    >
                      Cancel
                    </Button>
                  </div>
                ) : null
              }
            />
          </div>

          {/* Right: Treasury + Extras + Roster */}
          <div className="lg:col-span-2">
            <div className="space-y-10 lg:sticky lg:top-4 lg:self-start">
              {/* Treasury */}
              <Card className="py-0">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-baseline justify-between">
                    <Heading
                      level={3}
                      wrapperClassName="mb-0"
                      className="font-bold"
                    >
                      Treasury
                    </Heading>
                    <div className="text-right">
                      <span className="text-lg font-bold">
                        {formatFullGold(team.treasury_balance)}
                      </span>
                      <span
                        className="text-xs"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        {" "}
                        gp
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Staff & Extras */}
              <StaffExtrasCard>
                {/* Rerolls — functional */}
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">Rerolls</p>
                    <p className="text-xs text-[var(--accent-dim)]">
                      {formatGold(rerollPurchaseCost)} GP (2x creation cost)
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-5 text-center text-sm font-bold">
                      {team.rerolls}
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-7 w-7"
                      onClick={handlePurchaseReroll}
                      disabled={team.treasury_balance < rerollPurchaseCost}
                      aria-label="Purchase reroll"
                    >
                      <Plus className="size-3" />
                    </Button>
                  </div>
                </div>
              </StaffExtrasCard>

              {/* Current Roster */}
              <Card className="py-0">
                <CardContent className="pt-5 pb-4">
                  <Heading level={2} wrapperClassName="mb-3">
                    Roster ({alivePlayerCount} players)
                  </Heading>

                  {team.players.length === 0 ? (
                    <p
                      className="py-4 text-center text-sm"
                      style={{ color: "var(--muted-foreground)" }}
                    >
                      No players on roster
                    </p>
                  ) : (
                    <div
                      className="border-t"
                      style={{ borderColor: "var(--border)" }}
                    >
                      {team.players.map((player) => {
                        const isDead = player.status === "dead"
                        const isConfirmingFire = firePlayerId === player.id

                        return (
                          <div
                            key={player.id}
                            className={cn(
                              "flex items-center gap-2 border-b px-2 py-2 transition-[background] duration-100 hover:bg-[oklch(0.1_0.005_230)]",
                              isDead && "opacity-40",
                            )}
                            style={{ borderColor: "var(--border)" }}
                          >
                            <span
                              className="w-5 shrink-0 text-center text-xs font-bold"
                              style={{ color: "var(--muted-foreground)" }}
                            >
                              #{player.number}
                            </span>
                            <div className="min-w-0 flex-1">
                              <Link
                                href={leagueTeamPlayerPath(
                                  league.id,
                                  team.id,
                                  player.id,
                                )}
                                className="text-sm font-semibold hover:underline"
                              >
                                {player.name}
                              </Link>
                              <p
                                className="text-xs"
                                style={{ color: "var(--muted-foreground)" }}
                              >
                                {player.position} · {formatGold(player.cost)}
                              </p>
                            </div>

                            {isConfirmingFire ? (
                              <div className="flex items-center gap-1">
                                <Button
                                  type="button"
                                  variant="fire"
                                  size="sm"
                                  className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase"
                                  onClick={() => handleFire(player.id)}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="px-2 py-0.5 text-[10px]"
                                  onClick={() => setFirePlayerId(null)}
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="text-muted-foreground hover:text-destructive h-7 w-7"
                                disabled={!canFire || isDead}
                                onClick={() => setFirePlayerId(player.id)}
                                aria-label={`Fire ${player.name}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Roster footer */}
                  <div
                    className="mt-4 flex items-center justify-between border-t pt-3"
                    style={{ borderColor: "var(--border-heavy)" }}
                  >
                    <div>
                      <span
                        className="text-xs font-semibold tracking-wide uppercase"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        Team value
                      </span>
                      <p className="text-sm font-bold">
                        {formatFullGold(team.team_value)} gp
                      </p>
                    </div>
                    {!canFire && (
                      <span
                        className="text-xs"
                        style={{ color: "var(--muted-foreground)" }}
                      >
                        Min 11 players
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
